import { uniqueId } from 'lodash';
import React, { useState } from 'react';
import styled from 'styled-components';
import InfoIcon from '../../assets/svg/info.svg';
import SearchIcon from '../../assets/svg/search.svg';
import { getBreakpoint } from '../../base/style';
import { RepositTheme } from '../../base/theme';
import { useSearchInput } from './use-search-input';

const SearchWrapper = styled.div`
  position: relative;
  margin: 0 0 16px;

  @media screen and (min-width: ${getBreakpoint('lg')}) {
    margin: 0 0 40px;
  }
`;

const StyledSearch = styled.input<{ showHint: boolean }>`
  background: #fff;
  color: ${RepositTheme.colors.body};
  border: none;
  box-sizing: border-box;
  border-radius: 24px;
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.1);
  font-family: ${RepositTheme.typography.face.secondary};
  font-size: 16px;
  outline: none;
  position: relative;
  padding: 13px 24px;
  text-indent: 32px;
  width: 100%;

  ::placeholder {
    color: ${RepositTheme.colors.placeholder};
  }

  :focus {
    box-shadow: 0 0 0 2px ${RepositTheme.colors.bgHoverAccent}, 0 2px 3px 0 rgba(0, 0, 0, 0.1);
  }

  @media screen and (min-width: ${getBreakpoint('lg')}) {
    padding-right: ${(props) => (props.showHint ? '280px' : '24px')};
  }
`;

const SearchHint = styled.p<{ isVisible: boolean }>`
  align-items: center;
  background: ${RepositTheme.colors.bgHoverAccent};
  border-radius: 8px 18px 18px 8px;
  color: ${RepositTheme.colors.body2};
  display: flex;
  font-family: ${RepositTheme.typography.face.secondary};
  font-size: 13px;
  gap: 6px;
  line-height: 18px;
  margin: 8px 18px 0;
  max-width: calc(100% - 36px);
  padding: 4px 12px 4px 8px;
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
  width: fit-content;

  @media screen and (min-width: ${getBreakpoint('lg')}) {
    margin: 0;
    position: absolute;
    bottom: 4px;
    right: 4px;
    top: 4px;
  }
`;

const HintIcon = styled.img`
  flex-shrink: 0;
  height: 14px;
  width: 14px;
`;

const Icon = styled.div`
  background: transparent url(${SearchIcon});
  position: absolute;
  height: 20px;
  width: 20px;
  margin: 12px 18px;
  z-index: 1;
`;

interface SearchProps {
  placeholder?: string;
  minLength?: number;
  onChange: (input: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  value: string;
}

export default ({ onChange, onBlur, value, placeholder, minLength = 0 }: SearchProps) => {
  const { inputValue, handleChange } = useSearchInput(value, onChange, minLength);
  const [hintId] = useState(() => uniqueId('search-hint-'));
  const showHint = inputValue.trim().length < minLength;

  return (
    <SearchWrapper>
      <Icon />
      <StyledSearch
        aria-describedby={showHint ? hintId : undefined}
        showHint={showHint}
        minLength={minLength || undefined}
        placeholder={placeholder || 'Search'}
        onChange={(e) => handleChange(e.currentTarget.value)}
        onBlur={onBlur}
        value={inputValue}
      />
      {minLength > 0 && (
        <SearchHint id={hintId} isVisible={showHint}>
          <HintIcon src={InfoIcon} alt="" aria-hidden="true" />
          Enter at least {minLength} characters to search
        </SearchHint>
      )}
    </SearchWrapper>
  );
};
